import { useState } from 'react';
import Icon from './Icons';
import { parseIso, toIso, todayIso } from '../data/common';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const monthName = (d) => d.toLocaleString('en-US', { month: 'long', year: 'numeric' });
const firstOfMonth = (iso) => { const d = parseIso(iso); return new Date(d.getFullYear(), d.getMonth(), 1); };
const addMonths = (d, n) => new Date(d.getFullYear(), d.getMonth() + n, 1);

/** Day cells for one month, padded with blanks so the 1st falls under its weekday. */
function monthDays(month) {
  const blanks = Array.from({ length: month.getDay() }, () => null);
  const count = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  return [...blanks, ...Array.from({ length: count }, (_, i) => toIso(new Date(month.getFullYear(), month.getMonth(), i + 1)))];
}

function Month({ month, value, min, max, range, onSelect }) {
  const today = todayIso();
  const [start, end] = range || [];
  return (
    <div className="cal-month">
      <div className="cal-month-name">{monthName(month)}</div>
      <div className="cal-grid" role="grid">
        {WEEKDAYS.map((w) => <span key={w} className="cal-weekday">{w}</span>)}
        {monthDays(month).map((iso, i) => {
          if (!iso) return <span key={`b${i}`} />;
          const disabled = (min && iso < min) || (max && iso > max);
          const classes = [
            'cal-day',
            iso === value && 'selected',
            iso === today && 'today',
            start && end && iso > start && iso < end && 'in-range',
            start && end && iso === start && 'range-start',
            start && end && iso === end && 'range-end',
          ].filter(Boolean).join(' ');
          return (
            <button key={iso} type="button" className={classes} disabled={disabled}
              aria-pressed={iso === value} aria-label={parseIso(iso).toDateString()} onClick={() => onSelect(iso)}>
              {Number(iso.slice(8))}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Two-month date calendar (one month on small screens). `range` = [start, end] highlights the
 * days between, e.g. departure → return. Dates before `min` or after `max` can't be picked.
 * `stacked` lists `months` months one under another from the first allowed month, to scroll
 * through (phone bottom sheets), instead of paging with arrows.
 */
export default function Calendar({ value, min, max, range, onSelect, months = 2, stacked = false }) {
  const minMonth = min ? firstOfMonth(min) : null;
  const [view, setView] = useState(() => firstOfMonth(value || (min && min > todayIso() ? min : todayIso())));
  if (stacked) {
    const first = firstOfMonth(min && min > todayIso() ? min : todayIso());
    return (
      <div className="cal cal-stacked">
        {Array.from({ length: months }, (_, i) => (
          <Month key={i} month={addMonths(first, i)} value={value} min={min} max={max} range={range} onSelect={onSelect} />
        ))}
      </div>
    );
  }
  const canGoBack = !minMonth || view > minMonth;
  const canGoForward = !max || addMonths(view, months) <= firstOfMonth(max);
  return (
    <div className="cal">
      <button type="button" className="cal-nav prev" disabled={!canGoBack} aria-label="Previous month" onClick={() => setView(addMonths(view, -1))}>
        <Icon name="right" size={18} />
      </button>
      <button type="button" className="cal-nav next" disabled={!canGoForward} aria-label="Next month" onClick={() => setView(addMonths(view, 1))}>
        <Icon name="right" size={18} />
      </button>
      <div className="cal-months">
        {Array.from({ length: months }, (_, i) => (
          <Month key={i} month={addMonths(view, i)} value={value} min={min} max={max} range={range} onSelect={onSelect} />
        ))}
      </div>
    </div>
  );
}

/** Twelve-month picker for "month of travel" (value as YYYY-MM). */
export function MonthCalendar({ value, min, onSelect }) {
  const minMonth = min ? min.slice(0, 7) : null;
  const [year, setYear] = useState(() => Number((value || minMonth || todayIso()).slice(0, 4)));
  const minYear = minMonth ? Number(minMonth.slice(0, 4)) : null;
  return (
    <div className="cal cal-months-only">
      <div className="cal-year">
        <button type="button" className="cal-nav-inline" disabled={minYear != null && year <= minYear} aria-label="Previous year" onClick={() => setYear(year - 1)}>
          <Icon name="right" size={16} />
        </button>
        <b>{year}</b>
        <button type="button" className="cal-nav-inline next" aria-label="Next year" onClick={() => setYear(year + 1)}>
          <Icon name="right" size={16} />
        </button>
      </div>
      <div className="cal-month-grid">
        {Array.from({ length: 12 }, (_, m) => {
          const key = `${year}-${String(m + 1).padStart(2, '0')}`;
          return (
            <button key={key} type="button" className={`cal-month-btn ${key === value ? 'selected' : ''}`}
              disabled={minMonth != null && key < minMonth} aria-pressed={key === value} onClick={() => onSelect(key)}>
              {new Date(year, m, 1).toLocaleString('en-US', { month: 'short' })}
            </button>
          );
        })}
      </div>
    </div>
  );
}
